import {
  BoxGeometry,
  CanvasTexture,
  CapsuleGeometry,
  CylinderGeometry,
  Group,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  Quaternion,
  SphereGeometry,
  SRGBColorSpace,
  TorusGeometry,
  Vector3,
} from 'three';
import type { BufferGeometry, Object3D, BufferAttribute, Color } from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { FLAG, MODE, TRICK_KIND, decodeTrick } from '@thrasher/shared';
import type { Ride } from '@thrasher/shared';
import { rng } from './textures.js';

export interface AvatarLook {
  ride: Ride;
  color: string;
  seed: number;
}

export interface PoseInput {
  mode: number;
  flags: number;
  trick: number;
  phase: number;
  speed: number;
  steer: number;
  attack: { kind: 'punch' | 'kick'; t: number } | null;
  hit: number;
  protected: boolean;
  /** Ground height lookup for the loose board/bike during bails. */
  groundAt?: ((x: number, y: number, z: number) => number) | undefined;
}

const X = new Vector3(1, 0, 0);
const Y = new Vector3(0, 1, 0);
const Z = new Vector3(0, 0, 1);
const tmp = new Vector3();
const tmp2 = new Vector3();
const tmp3 = new Vector3();
const tmpQ = new Quaternion();
const tmpM = new Matrix4();

const THIGH = 0.43;
const SHIN = 0.43;
const UPPER_ARM = 0.27;
const FOREARM = 0.25;

const SKIN_TONES = ['#f1c7a5', '#e0a883', '#c68863', '#9a6344', '#6e442c', '#f6d5bd'];
const PANTS = ['#27324a', '#1d1f24', '#4a4238', '#2f3b2c', '#5a5f6b', '#3b2a44'];
const SHOES = ['#f4f4f4', '#1a1a1a', '#d23a2f', '#2a6fd6', '#f2c22e'];

function capsule(radius: number, length: number, material: MeshStandardMaterial): Mesh {
  const mesh = new Mesh(new CapsuleGeometry(radius, length, 5, 10), material);
  mesh.castShadow = true;
  return mesh;
}

/** Places a Y-aligned mesh centred between two points. */
function span(mesh: Object3D, a: Vector3, b: Vector3): void {
  mesh.position.addVectors(a, b).multiplyScalar(0.5);
  tmp.subVectors(b, a);
  const length = tmp.length();
  if (length < 1e-5) return;
  mesh.quaternion.setFromUnitVectors(Y, tmp.divideScalar(length));
}

/** Orients an object so its +Y is `up` and +Z is as close to `forward` as possible. */
function orient(object: Object3D, up: Vector3, forward: Vector3): void {
  const y = tmp.copy(up).normalize();
  const x = tmp2.crossVectors(y, forward);
  if (x.lengthSq() < 1e-6) x.set(1, 0, 0);
  x.normalize();
  const z = tmp3.crossVectors(x, y).normalize();
  tmpM.makeBasis(x, y, z);
  object.quaternion.setFromRotationMatrix(tmpM);
}

/** Two-bone IK: writes the middle joint; returns the reachable end point. */
function solveLimb(
  root: Vector3,
  target: Vector3,
  l1: number,
  l2: number,
  hint: Vector3,
  mid: Vector3,
  end: Vector3,
): void {
  const d = tmp.subVectors(target, root);
  const dist = Math.max(0.01, Math.min(d.length(), l1 + l2 - 1e-4));
  const dir = d.normalize();
  end.copy(root).addScaledVector(dir, dist);
  const a = (l1 * l1 - l2 * l2 + dist * dist) / (2 * dist);
  const h = Math.sqrt(Math.max(0, l1 * l1 - a * a));
  const bend = tmp2.copy(hint).addScaledVector(dir, -hint.dot(dir));
  if (bend.lengthSq() < 1e-6) bend.set(0, 0, 1).addScaledVector(dir, -dir.z);
  bend.normalize();
  mid.copy(root).addScaledVector(dir, a).addScaledVector(bend, h);
}

function boardGraphic(color: string, seed: number): CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 256;
  const ctx = canvas.getContext('2d')!;
  const random = rng(seed);
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, 64, 256);
  ctx.fillStyle = '#111';
  for (let i = 0; i < 6; i += 1) {
    ctx.fillRect(0, 20 + i * 38 + random() * 8, 64, 6 + random() * 8);
  }
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.arc(32, 128, 18, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(32, 128, 10, 0, Math.PI * 2);
  ctx.fill();
  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  return texture;
}

function buildSkateboard(color: string, seed: number): { group: Group; wheels: Mesh[] } {
  const group = new Group();
  const deck = new BoxGeometry(0.21, 0.014, 0.82, 1, 1, 32);
  const pos = deck.getAttribute('position') as BufferAttribute;
  for (let i = 0; i < pos.count; i += 1) {
    const z = pos.getZ(i);
    const az = Math.abs(z);
    let y = pos.getY(i);
    if (az > 0.27) y += (az - 0.27) * 0.34;
    let x = pos.getX(i);
    if (az > 0.33) x *= Math.sqrt(Math.max(0.08, 1 - ((az - 0.33) / 0.085) ** 2));
    pos.setXYZ(i, x, y, z);
  }
  deck.computeVertexNormals();
  const wood = new MeshStandardMaterial({ color: '#c9a36b', roughness: 0.7 });
  const grip = new MeshStandardMaterial({ color: '#1b1b1d', roughness: 0.95 });
  const graphic = new MeshStandardMaterial({ map: boardGraphic(color, seed), roughness: 0.5 });
  const deckMesh = new Mesh(deck, [wood, wood, grip, graphic, wood, wood]);
  deckMesh.position.y = 0.078;
  deckMesh.castShadow = true;
  group.add(deckMesh);
  const metal = new MeshStandardMaterial({ color: '#c9ccd2', metalness: 0.9, roughness: 0.3 });
  const trucks = mergeGeometries([
    new BoxGeometry(0.06, 0.02, 0.09).translate(0, 0.062, 0.24),
    new BoxGeometry(0.06, 0.02, 0.09).translate(0, 0.062, -0.24),
    new CylinderGeometry(0.009, 0.009, 0.2, 6).rotateZ(Math.PI / 2).translate(0, 0.03, 0.24),
    new CylinderGeometry(0.009, 0.009, 0.2, 6).rotateZ(Math.PI / 2).translate(0, 0.03, -0.24),
    new BoxGeometry(0.04, 0.035, 0.04).translate(0, 0.045, 0.24),
    new BoxGeometry(0.04, 0.035, 0.04).translate(0, 0.045, -0.24),
  ])!;
  const truckMesh = new Mesh(trucks, metal);
  truckMesh.castShadow = true;
  group.add(truckMesh);
  const wheelMaterial = new MeshStandardMaterial({ color: '#f1ead8', roughness: 0.45 });
  const wheelGeometry = new CylinderGeometry(0.028, 0.028, 0.034, 14).rotateZ(Math.PI / 2);
  const wheels: Mesh[] = [];
  for (const x of [-0.095, 0.095]) {
    for (const z of [-0.24, 0.24]) {
      const wheel = new Mesh(wheelGeometry, wheelMaterial);
      wheel.position.set(x, 0.028, z);
      wheel.castShadow = true;
      group.add(wheel);
      wheels.push(wheel);
    }
  }
  return { group, wheels };
}

function tubeBetween(a: Vector3, b: Vector3, radius: number): BufferGeometry {
  const length = a.distanceTo(b);
  const geometry = new CylinderGeometry(radius, radius, length, 8);
  const q = new Quaternion().setFromUnitVectors(Y, tmp.subVectors(b, a).normalize());
  geometry.applyQuaternion(q);
  const mid = a.clone().add(b).multiplyScalar(0.5);
  geometry.translate(mid.x, mid.y, mid.z);
  return geometry;
}

interface Bike {
  group: Group;
  frame: Group;
  front: Group;
  bars: Group;
  cranks: Group;
  wheels: Group[];
  headTop: Vector3;
  headBottom: Vector3;
}

function wheelGroup(radius: number, tire: MeshStandardMaterial, rim: MeshStandardMaterial): Group {
  const group = new Group();
  const tireMesh = new Mesh(
    new TorusGeometry(radius - 0.03, 0.032, 8, 32).rotateY(Math.PI / 2),
    tire,
  );
  tireMesh.castShadow = true;
  group.add(tireMesh);
  const spokes: BufferGeometry[] = [
    new TorusGeometry(radius - 0.06, 0.012, 6, 32).rotateY(Math.PI / 2),
  ];
  for (let i = 0; i < 12; i += 1) {
    const angle = (i / 12) * Math.PI * 2;
    spokes.push(
      tubeBetween(
        new Vector3(0, 0, 0),
        new Vector3(0, Math.cos(angle) * (radius - 0.06), Math.sin(angle) * (radius - 0.06)),
        0.003,
      ),
    );
  }
  spokes.push(new CylinderGeometry(0.03, 0.03, 0.1, 10).rotateZ(Math.PI / 2));
  group.add(new Mesh(mergeGeometries(spokes)!, rim));
  return group;
}

function buildBmx(color: string): Bike {
  const group = new Group();
  const frame = new Group();
  const front = new Group();
  const bars = new Group();
  const cranks = new Group();
  const frameMaterial = new MeshStandardMaterial({ color, metalness: 0.6, roughness: 0.3 });
  const chrome = new MeshStandardMaterial({ color: '#d7dbe2', metalness: 1, roughness: 0.2 });
  const black = new MeshStandardMaterial({ color: '#16171a', roughness: 0.8 });
  const r = 0.255;
  const rear = new Vector3(0, r, -0.49);
  const frontAxle = new Vector3(0, r, 0.49);
  const bb = new Vector3(0, 0.3, -0.03);
  const seat = new Vector3(0, 0.72, -0.27);
  const headTop = new Vector3(0, 0.84, 0.35);
  const headBottom = new Vector3(0, 0.68, 0.4);
  const tubes = mergeGeometries([
    tubeBetween(seat, headTop, 0.022),
    tubeBetween(bb, headBottom, 0.026),
    tubeBetween(bb, seat, 0.022),
    tubeBetween(seat, rear.clone().add(new Vector3(0.05, 0, 0)), 0.013),
    tubeBetween(seat, rear.clone().add(new Vector3(-0.05, 0, 0)), 0.013),
    tubeBetween(bb, rear.clone().add(new Vector3(0.05, 0, 0)), 0.014),
    tubeBetween(bb, rear.clone().add(new Vector3(-0.05, 0, 0)), 0.014),
    tubeBetween(headTop, headBottom, 0.03),
  ])!;
  const frameMesh = new Mesh(tubes, frameMaterial);
  frameMesh.castShadow = true;
  frame.add(frameMesh);
  const seatMesh = new Mesh(
    new BoxGeometry(0.1, 0.05, 0.24).rotateX(0.15).translate(0, 0.8, -0.3),
    black,
  );
  seatMesh.castShadow = true;
  frame.add(seatMesh, new Mesh(tubeBetween(seat, new Vector3(0, 0.79, -0.3), 0.014), chrome));
  const pegs = mergeGeometries([
    new CylinderGeometry(0.02, 0.02, 0.24, 8).rotateZ(Math.PI / 2).translate(0, r, -0.49),
  ])!;
  frame.add(new Mesh(pegs, chrome));
  const rearWheel = wheelGroup(r, black, chrome);
  rearWheel.position.copy(rear);
  frame.add(rearWheel);
  cranks.position.copy(bb);
  cranks.add(
    new Mesh(
      mergeGeometries([
        new BoxGeometry(0.02, 0.17, 0.03).translate(0, -0.085, 0).translate(0.07, 0, 0),
        new BoxGeometry(0.02, 0.17, 0.03).translate(0, 0.085, 0).translate(-0.07, 0, 0),
        new BoxGeometry(0.1, 0.02, 0.06).translate(0.12, -0.17, 0),
        new BoxGeometry(0.1, 0.02, 0.06).translate(-0.12, 0.17, 0),
        new CylinderGeometry(0.08, 0.08, 0.01, 18).rotateZ(Math.PI / 2).translate(0.05, 0, 0),
      ])!,
      chrome,
    ),
  );
  frame.add(cranks);

  const fork = mergeGeometries([
    tubeBetween(headBottom, frontAxle.clone().add(new Vector3(0.05, 0, 0)), 0.016),
    tubeBetween(headBottom, frontAxle.clone().add(new Vector3(-0.05, 0, 0)), 0.016),
    new CylinderGeometry(0.02, 0.02, 0.24, 8).rotateZ(Math.PI / 2).translate(0, r, 0.49),
  ])!;
  front.add(new Mesh(fork, chrome));
  const frontWheel = wheelGroup(r, black, chrome);
  frontWheel.position.copy(frontAxle);
  front.add(frontWheel);
  const barGeometry = mergeGeometries([
    tubeBetween(headTop, new Vector3(0, 0.93, 0.33), 0.018),
    tubeBetween(new Vector3(-0.3, 1.0, 0.31), new Vector3(0.3, 1.0, 0.31), 0.013),
    tubeBetween(new Vector3(-0.14, 0.92, 0.34), new Vector3(-0.2, 1.0, 0.31), 0.012),
    tubeBetween(new Vector3(0.14, 0.92, 0.34), new Vector3(0.2, 1.0, 0.31), 0.012),
    tubeBetween(new Vector3(-0.14, 0.92, 0.34), new Vector3(0.14, 0.92, 0.34), 0.012),
  ])!;
  bars.add(new Mesh(barGeometry, chrome));
  bars.add(
    new Mesh(
      mergeGeometries([
        tubeBetween(new Vector3(-0.3, 1.0, 0.31), new Vector3(-0.2, 1.0, 0.31), 0.02),
        tubeBetween(new Vector3(0.3, 1.0, 0.31), new Vector3(0.2, 1.0, 0.31), 0.02),
      ])!,
      black,
    ),
  );
  front.add(bars);
  group.add(frame, front);
  group.traverse((child) => {
    if ((child as Mesh).isMesh) child.castShadow = true;
  });
  return {
    group,
    frame,
    front,
    bars,
    cranks,
    wheels: [rearWheel, frontWheel],
    headTop,
    headBottom,
  };
}

function buildSkate(color: string): Group {
  const group = new Group();
  const boot = new MeshStandardMaterial({ color: '#1f2126', roughness: 0.6 });
  const cuff = new MeshStandardMaterial({ color, roughness: 0.4, metalness: 0.2 });
  const frame = new MeshStandardMaterial({ color: '#b9bec8', metalness: 0.9, roughness: 0.3 });
  const wheel = new MeshStandardMaterial({ color: '#8fd8ff', roughness: 0.4 });
  const shell = new Mesh(new BoxGeometry(0.11, 0.12, 0.27).translate(0, 0.11, 0.02), boot);
  const top = new Mesh(new CylinderGeometry(0.065, 0.07, 0.12, 12).translate(0, 0.2, -0.04), cuff);
  const rails = new Mesh(new BoxGeometry(0.03, 0.035, 0.3).translate(0, 0.05, 0.02), frame);
  const wheels: BufferGeometry[] = [];
  for (let i = 0; i < 4; i += 1) {
    wheels.push(
      new CylinderGeometry(0.036, 0.036, 0.024, 12)
        .rotateZ(Math.PI / 2)
        .translate(0, 0.036, -0.1 + i * 0.075),
    );
  }
  const wheelMesh = new Mesh(mergeGeometries(wheels)!, wheel);
  group.add(shell, top, rails, wheelMesh);
  group.traverse((child) => {
    if ((child as Mesh).isMesh) child.castShadow = true;
  });
  return group;
}

/**
 * A code-built rider: IK legs/arms over a board, BMX or inline skates, with procedural
 * poses for riding, pushing, grinding (balance lean), tricks, attacks and bails.
 */
export class RiderAvatar {
  public readonly root = new Group();
  private readonly spinner = new Group();
  private readonly vehicle = new Group();
  private readonly materials: MeshStandardMaterial[] = [];
  private readonly baseEmissive: Color[] = [];
  private readonly parts: Record<string, Object3D> = {};
  private readonly skate: { group: Group; wheels: Mesh[] } | null = null;
  private readonly bike: Bike | null = null;
  private readonly boots: Group[] = [];
  private readonly look: AvatarLook;
  private time = 0;
  private crankAngle = 0;
  private pushPhase = 0;
  private wheelSpin = 0;
  private crouch = 0.15;
  private lean = 0;
  private attackKind: 'punch' | 'kick' | null = null;
  private attackT = 0;
  private flail = 0;
  private looseVehicle: { velocity: Vector3; spin: Vector3; grounded: boolean } | null = null;

  // Joint scratch.
  private readonly j = {
    pelvis: new Vector3(),
    chest: new Vector3(),
    neck: new Vector3(),
    head: new Vector3(),
    hipL: new Vector3(),
    hipR: new Vector3(),
    kneeL: new Vector3(),
    kneeR: new Vector3(),
    footL: new Vector3(),
    footR: new Vector3(),
    shoulderL: new Vector3(),
    shoulderR: new Vector3(),
    elbowL: new Vector3(),
    elbowR: new Vector3(),
    handL: new Vector3(),
    handR: new Vector3(),
  };

  public constructor(look: AvatarLook) {
    this.look = look;
    const random = rng(look.seed);
    const skin = this.material(SKIN_TONES[Math.floor(random() * SKIN_TONES.length)]!, 0.6);
    const shirt = this.material(look.color, 0.75);
    const pants = this.material(PANTS[Math.floor(random() * PANTS.length)]!, 0.85);
    const shoes = this.material(SHOES[Math.floor(random() * SHOES.length)]!, 0.6);
    const helmet = this.material(look.color, 0.25, 0.3);
    const dark = this.material('#15161a', 0.5);

    this.root.add(this.spinner);
    this.spinner.add(this.vehicle);

    const add = (name: string, mesh: Object3D) => {
      this.parts[name] = mesh;
      this.spinner.add(mesh);
    };
    add('thighL', capsule(0.088, THIGH - 0.1, pants));
    add('thighR', capsule(0.088, THIGH - 0.1, pants));
    add('shinL', capsule(0.074, SHIN - 0.08, pants));
    add('shinR', capsule(0.074, SHIN - 0.08, pants));
    add('upperL', capsule(0.068, UPPER_ARM - 0.08, shirt));
    add('upperR', capsule(0.068, UPPER_ARM - 0.08, shirt));
    add('foreL', capsule(0.052, FOREARM - 0.06, skin));
    add('foreR', capsule(0.052, FOREARM - 0.06, skin));
    const torso = capsule(0.17, 0.24, shirt);
    torso.scale.set(1.3, 1, 0.85);
    add('torso', torso);
    const pelvis = capsule(0.13, 0.08, pants);
    pelvis.scale.set(1.35, 1, 0.9);
    add('pelvis', pelvis);
    add('neck', capsule(0.05, 0.06, skin));
    const head = new Group();
    const skull = new Mesh(new SphereGeometry(0.125, 18, 14), skin);
    skull.castShadow = true;
    head.add(skull);
    if (look.ride === 'skateboard') {
      const beanie = new Mesh(
        new SphereGeometry(0.122, 16, 10, 0, Math.PI * 2, 0, Math.PI / 1.9),
        helmet,
      );
      beanie.position.y = 0.012;
      beanie.castShadow = true;
      head.add(beanie);
    } else {
      const shell = new Mesh(
        new SphereGeometry(0.135, 18, 12, 0, Math.PI * 2, 0, Math.PI / 1.75),
        helmet,
      );
      shell.position.y = 0.01;
      shell.castShadow = true;
      head.add(shell);
      if (look.ride === 'bmx') {
        const visor = new Mesh(new BoxGeometry(0.2, 0.02, 0.09).translate(0, 0.06, 0.12), dark);
        head.add(visor);
        const chin = new Mesh(
          new TorusGeometry(0.1, 0.022, 6, 16, Math.PI)
            .rotateX(Math.PI / 2)
            .rotateY(Math.PI)
            .translate(0, -0.06, 0.02),
          helmet,
        );
        head.add(chin);
      }
    }
    const eyes = mergeGeometries([
      new SphereGeometry(0.016, 8, 6).translate(0.04, 0.02, 0.105),
      new SphereGeometry(0.016, 8, 6).translate(-0.04, 0.02, 0.105),
    ])!;
    head.add(new Mesh(eyes, dark));
    add('head', head);
    add('handL', new Mesh(new SphereGeometry(0.048, 10, 8), skin));
    add('handR', new Mesh(new SphereGeometry(0.048, 10, 8), skin));

    if (look.ride === 'inline') {
      for (let i = 0; i < 2; i += 1) {
        const boot = buildSkate(look.color);
        this.boots.push(boot);
        this.spinner.add(boot);
      }
    } else {
      const shoeGeometry = new CapsuleGeometry(0.052, 0.17, 4, 8).rotateX(Math.PI / 2);
      add('shoeL', new Mesh(shoeGeometry, shoes));
      add('shoeR', new Mesh(shoeGeometry, shoes));
    }
    if (look.ride === 'skateboard') {
      this.skate = buildSkateboard(look.color, look.seed);
      this.vehicle.add(this.skate.group);
    } else if (look.ride === 'bmx') {
      this.bike = buildBmx(look.color);
      this.vehicle.add(this.bike.group);
    }
    this.root.traverse((child) => {
      if ((child as Mesh).isMesh) child.castShadow = true;
    });
  }

  private material(color: string, roughness: number, metalness = 0): MeshStandardMaterial {
    const material = new MeshStandardMaterial({ color, roughness, metalness });
    this.materials.push(material);
    this.baseEmissive.push(material.emissive.clone());
    return material;
  }

  /** Starts an attack animation (local press or remote flag edge). */
  public attack(kind: 'punch' | 'kick'): void {
    this.attackKind = kind;
    this.attackT = 0;
  }

  public update(dt: number, input: PoseInput): void {
    this.time += dt;
    const ride = this.look.ride;
    const { kind, variant } = decodeTrick(input.trick);
    const mode = input.mode;
    const bailing = mode === MODE.bail || mode === MODE.down;
    const grinding = mode === MODE.grind;
    const air = mode === MODE.air;
    const flags = input.flags;

    if (input.attack) {
      this.attackKind = input.attack.kind;
      this.attackT = input.attack.t;
    } else if (this.attackKind) {
      this.attackT += dt / (this.attackKind === 'punch' ? 0.26 : 0.38);
      if (this.attackT >= 1) this.attackKind = null;
    }

    // Target crouch.
    let crouchTarget = 0.14 + Math.min(0.12, input.speed * 0.008);
    if (flags & FLAG.crouch) crouchTarget = 0.62;
    if (air) crouchTarget = 0.38;
    if (grinding) crouchTarget = 0.32 + Math.abs(input.phase) * 0.15;
    if (flags & FLAG.grab || kind === TRICK_KIND.grab) crouchTarget = 0.9;
    if (bailing) crouchTarget = 0.45;
    this.crouch += (crouchTarget - this.crouch) * Math.min(1, dt * 12);
    const leanTarget = grinding ? input.phase * 0.55 : air ? 0 : input.steer * 0.3;
    this.lean += (leanTarget - this.lean) * Math.min(1, dt * 10);
    this.flail = bailing ? this.flail + dt * 14 : 0;

    const j = this.j;
    const facing = new Vector3(0, 0, 1);
    if (ride === 'skateboard') {
      // Regular stance faces the rider's right; pushing and punching open up toward travel.
      const open =
        (flags & FLAG.push ? 0.45 : 0) +
        (this.attackKind ? 0.6 * Math.sin(Math.PI * Math.min(1, this.attackT)) : 0);
      facing.set(-1, 0, 0).lerp(Z, Math.min(0.85, open)).normalize();
    }
    const left = new Vector3().crossVectors(Y, facing).normalize();

    // Feet.
    let footY = ride === 'skateboard' ? 0.09 : ride === 'inline' ? 0.1 : 0.3;
    const footL = j.footL;
    const footR = j.footR;
    let footDirL = facing.clone();
    let footDirR = facing.clone();
    if (ride === 'skateboard') {
      footL.set(0, footY, 0.21);
      footR.set(0, footY, -0.2);
      if (flags & FLAG.push && mode === MODE.ground) {
        this.pushPhase += dt * 6.5;
        const s = (this.pushPhase % (Math.PI * 2)) / (Math.PI * 2);
        const planted = s < 0.55;
        const z = planted ? 0.25 - (s / 0.55) * 0.75 : -0.5 + ((s - 0.55) / 0.45) * 0.75;
        footR.set(-0.2, planted ? 0.0 : 0.09 + Math.sin(((s - 0.55) / 0.45) * Math.PI) * 0.1, z);
        footDirL = Z.clone();
      }
    } else if (ride === 'bmx') {
      if (flags & FLAG.push && mode === MODE.ground)
        this.crankAngle += dt * Math.max(4, input.speed * 0.9);
      else
        this.crankAngle +=
          (Math.round(this.crankAngle / Math.PI) * Math.PI - this.crankAngle) * Math.min(1, dt * 4);
      const c = this.crankAngle;
      footR.set(0.13, 0.3 - Math.cos(c) * 0.17 + 0.03, -0.03 + Math.sin(c) * 0.17);
      footL.set(-0.13, 0.3 + Math.cos(c) * 0.17 + 0.03, -0.03 - Math.sin(c) * 0.17);
      footY = 0.3;
    } else {
      const striding = flags & FLAG.push && mode === MODE.ground;
      if (striding) this.pushPhase += dt * 5.5;
      const s = striding ? Math.sin(this.pushPhase) : 0;
      footL.set(0.14 + Math.max(0, s) * 0.18, footY + Math.max(0, -s) * 0.06, s * 0.22);
      footR.set(-0.14 - Math.max(0, -s) * 0.18, footY + Math.max(0, s) * 0.06, -s * 0.22);
      if (grinding) {
        footL.set(0.02, footY, 0.2);
        footR.set(-0.02, footY, -0.2);
        footDirL = new Vector3(1, 0, 0.2).normalize();
        if (variant === 1) footDirR = new Vector3(-1, 0, 0.2).normalize();
      }
    }

    // Pelvis height from crouch; lean shifts the whole upper body.
    const legLength = THIGH + SHIN;
    let pelvisHeight = footY + legLength * (0.97 - this.crouch * 0.42);
    if (ride === 'bmx') pelvisHeight = 0.98 - this.crouch * 0.3;
    j.pelvis
      .set(0, pelvisHeight, ride === 'bmx' ? -0.12 : 0)
      .addScaledVector(facing, -this.crouch * 0.08);
    const leanVector = new Vector3(-this.lean, 0, 0);
    j.pelvis.addScaledVector(leanVector, 0.12);

    // Tricks move feet: flips lift them off the board.
    if (kind === TRICK_KIND.flip && ride === 'skateboard') {
      const lift = Math.sin(Math.PI * input.phase) * 0.26;
      footL.y += lift;
      footR.y += lift;
    }
    if ((flags & FLAG.grab || kind === TRICK_KIND.grab) && ride === 'bmx') {
      if (variant === 1) footL.set(0.28, 0.55, -0.05);
      if (variant === 2) {
        footL.set(-0.1, 1.0, -0.8);
        footR.set(0.1, 1.0, -0.8);
        j.pelvis.set(0, 1.08, -0.35);
      }
    }

    // Kick: one leg swings toward travel.
    if (this.attackKind === 'kick') {
      const t = Math.min(1, this.attackT);
      const strike = Math.sin(Math.PI * Math.min(1, t * 1.4));
      const kickFoot = ride === 'skateboard' ? footL : footR;
      kickFoot.lerp(
        new Vector3(ride === 'skateboard' ? -0.1 : -0.25, pelvisHeight - 0.05, 0.85),
        strike,
      );
      j.pelvis.z -= strike * 0.08;
    }

    // Spine.
    const spine = new Vector3(0, 1, 0)
      .addScaledVector(facing, ride === 'bmx' ? 0.55 : 0.18)
      .addScaledVector(leanVector, 0.6);
    if (air) spine.addScaledVector(facing, 0.2);
    if (this.attackKind === 'kick') spine.addScaledVector(Z, -0.35);
    if (bailing)
      spine.add(new Vector3(Math.sin(this.flail * 0.7) * 0.4, 0, Math.cos(this.flail * 0.5) * 0.4));
    spine.normalize();
    j.chest.copy(j.pelvis).addScaledVector(spine, 0.36);
    j.neck.copy(j.chest).addScaledVector(spine, 0.19);
    j.head.copy(j.neck).addScaledVector(spine, 0.12);

    // Shoulders and hips follow the torso's own left axis.
    const torsoLeft = new Vector3().crossVectors(spine, facing).normalize();
    if (torsoLeft.lengthSq() < 0.5) torsoLeft.copy(left);
    j.shoulderL.copy(j.chest).addScaledVector(torsoLeft, 0.2).addScaledVector(spine, -0.05);
    j.shoulderR.copy(j.chest).addScaledVector(torsoLeft, -0.2).addScaledVector(spine, -0.05);
    j.hipL.copy(j.pelvis).addScaledVector(left, 0.1);
    j.hipR.copy(j.pelvis).addScaledVector(left, -0.1);

    // Hands.
    const sway = Math.sin(this.time * 2.1) * 0.04;
    const armsOut = grinding ? 0.25 + Math.abs(input.phase) * 0.35 : air ? 0.3 : 0.12;
    j.handL
      .copy(j.shoulderL)
      .addScaledVector(torsoLeft, 0.22 + armsOut)
      .add(new Vector3(0, -0.42 + armsOut * 0.6 + sway, 0))
      .addScaledVector(facing, 0.08);
    j.handR
      .copy(j.shoulderR)
      .addScaledVector(torsoLeft, -0.22 - armsOut)
      .add(new Vector3(0, -0.42 + armsOut * 0.6 - sway, 0))
      .addScaledVector(facing, 0.08);
    if (grinding) {
      // Balance: the arms counter the lean.
      j.handL.y += input.phase * 0.25;
      j.handR.y -= input.phase * 0.25;
    }
    if (ride === 'bmx' && !bailing) {
      j.handL.set(0.26, 1.0, 0.31);
      j.handR.set(-0.26, 1.0, 0.31);
      if ((flags & FLAG.grab || kind === TRICK_KIND.grab) && variant === 0) {
        j.handL.set(0.6, 1.25, 0.1);
        j.handR.set(-0.6, 1.25, 0.1);
      }
    }
    if ((flags & FLAG.grab || kind === TRICK_KIND.grab) && ride !== 'bmx') {
      const hand = variant % 2 === 0 ? j.handR : j.handL;
      const target =
        ride === 'skateboard'
          ? new Vector3(
              variant >= 2 ? 0.1 : -0.1,
              footY + 0.03,
              variant === 1 || variant === 2 ? 0.1 : -0.05,
            )
          : (variant % 2 === 0 ? footL : footR).clone().add(new Vector3(0, 0.12, 0.1));
      hand.copy(target);
    }
    if (bailing) {
      j.handL
        .copy(j.shoulderL)
        .add(
          new Vector3(
            Math.sin(this.flail) * 0.4 + 0.3,
            Math.cos(this.flail * 1.3) * 0.4,
            Math.sin(this.flail * 0.8) * 0.3,
          ),
        );
      j.handR
        .copy(j.shoulderR)
        .add(
          new Vector3(
            -Math.sin(this.flail * 1.1) * 0.4 - 0.3,
            Math.cos(this.flail) * 0.4,
            Math.cos(this.flail * 0.9) * 0.3,
          ),
        );
      footL.add(new Vector3(Math.sin(this.flail * 0.6) * 0.2, 0.2, 0));
      footR.add(new Vector3(-Math.sin(this.flail * 0.7) * 0.2, 0.2, 0));
    }
    if (this.attackKind === 'punch') {
      const t = Math.min(1, this.attackT);
      const windup = t < 0.3 ? t / 0.3 : 1;
      const strike = t < 0.3 ? 0 : t < 0.6 ? (t - 0.3) / 0.3 : 1 - (t - 0.6) / 0.4;
      const fist = ride === 'bmx' ? j.handL : j.handR;
      const shoulder = ride === 'bmx' ? j.shoulderL : j.shoulderR;
      const back = shoulder.clone().add(new Vector3(0, -0.1, -0.25));
      const out = shoulder.clone().add(new Vector3(0, 0.02, 0.62));
      fist.lerp(back, windup * (1 - strike)).lerp(out, strike);
    }

    // Solve limbs.
    const kneeHint =
      ride === 'bmx' ? new Vector3(0, 0.2, 1) : facing.clone().add(new Vector3(0, -0.2, 0));
    solveLimb(j.hipL, footL, THIGH, SHIN, kneeHint, j.kneeL, footL);
    solveLimb(j.hipR, footR, THIGH, SHIN, kneeHint, j.kneeR, footR);
    const elbowHint = new Vector3(0, -1, -0.4);
    solveLimb(
      j.shoulderL,
      j.handL,
      UPPER_ARM,
      FOREARM,
      elbowHint.clone().addScaledVector(torsoLeft, 0.6),
      j.elbowL,
      j.handL,
    );
    solveLimb(
      j.shoulderR,
      j.handR,
      UPPER_ARM,
      FOREARM,
      elbowHint.clone().addScaledVector(torsoLeft, -0.6),
      j.elbowR,
      j.handR,
    );

    const p = this.parts;
    span(p.thighL!, j.hipL, j.kneeL);
    span(p.thighR!, j.hipR, j.kneeR);
    span(p.shinL!, j.kneeL, footL);
    span(p.shinR!, j.kneeR, footR);
    span(p.upperL!, j.shoulderL, j.elbowL);
    span(p.upperR!, j.shoulderR, j.elbowR);
    span(p.foreL!, j.elbowL, j.handL);
    span(p.foreR!, j.elbowR, j.handR);
    span(p.neck!, j.chest, j.head);
    p.torso!.position.addVectors(j.pelvis, j.chest)
      .multiplyScalar(0.5)
      .addScaledVector(spine, 0.04);
    orient(p.torso!, spine, facing);
    p.pelvis!.position.copy(j.pelvis);
    orient(p.pelvis!, spine, facing);
    p.head!.position.copy(j.head);
    const look = new Vector3(0, 0, 1).lerp(facing, ride === 'skateboard' ? 0.25 : 0.1);
    orient(p.head!, spine, look);
    p.handL!.position.copy(j.handL);
    p.handR!.position.copy(j.handR);
    if (p.shoeL && p.shoeR) {
      p.shoeL.position
        .copy(footL)
        .add(new Vector3(0, 0.03, 0))
        .addScaledVector(footDirL, 0.05);
      p.shoeR.position
        .copy(footR)
        .add(new Vector3(0, 0.03, 0))
        .addScaledVector(footDirR, 0.05);
      p.shoeL.quaternion.setFromUnitVectors(Z, footDirL);
      p.shoeR.quaternion.setFromUnitVectors(Z, footDirR);
    }
    this.boots.forEach((boot, index) => {
      const foot = index === 0 ? footL : footR;
      const dir = index === 0 ? footDirL : footDirR;
      boot.position.copy(foot).add(new Vector3(0, -0.1, 0));
      boot.quaternion.setFromUnitVectors(Z, dir);
    });

    this.poseVehicle(dt, input, kind, variant, bailing, grinding);
    this.poseSpinner(input, kind, variant);
    this.applyTint(input);
  }

  private poseVehicle(
    dt: number,
    input: PoseInput,
    kind: number,
    variant: number,
    bailing: boolean,
    grinding: boolean,
  ): void {
    this.wheelSpin += (input.speed / 0.03) * dt;
    if (this.skate) {
      for (const wheel of this.skate.wheels) wheel.rotation.x = this.wheelSpin;
    }
    if (this.bike) {
      for (const wheel of this.bike.wheels) wheel.rotation.x = this.wheelSpin * (0.03 / 0.255);
      this.bike.cranks.rotation.x = -this.crankAngle;
    }
    if (bailing && this.look.ride !== 'inline') {
      this.updateLooseVehicle(dt, input);
      return;
    }
    if (this.looseVehicle) {
      this.looseVehicle = null;
      this.spinner.add(this.vehicle);
    }
    this.vehicle.position.set(0, 0, 0);
    this.vehicle.quaternion.identity();
    if (this.skate) {
      const t = input.phase;
      if (kind === TRICK_KIND.flip) {
        const lift = Math.sin(Math.PI * t) * 0.18;
        this.vehicle.position.y = lift;
        const angle = Math.PI * 2 * t;
        if (variant === 0) this.vehicle.quaternion.setFromAxisAngle(Z, angle);
        else if (variant === 1) this.vehicle.quaternion.setFromAxisAngle(Z, -angle);
        else if (variant === 2)
          this.vehicle.quaternion
            .setFromAxisAngle(Y, angle)
            .multiply(tmpQ.setFromAxisAngle(Z, angle));
        else this.vehicle.quaternion.setFromAxisAngle(X, angle);
        this.vehicle.position.y += 0.1;
      } else if (kind === TRICK_KIND.grind) {
        if (variant === 1) this.vehicle.quaternion.setFromAxisAngle(Y, Math.PI / 2);
        else if (variant === 2)
          this.vehicle.quaternion.setFromAxisAngle(Y, 0.4).multiply(tmpQ.setFromAxisAngle(X, 0.15));
        else if (variant === 3) this.vehicle.quaternion.setFromAxisAngle(X, 0.2);
        this.vehicle.position.y = -0.05;
      } else if (kind === TRICK_KIND.grab && variant === 2) {
        this.vehicle.quaternion.setFromAxisAngle(X, -0.5);
      }
    }
    if (this.bike) {
      const bike = this.bike;
      bike.frame.quaternion.identity();
      bike.frame.position.set(0, 0, 0);
      bike.bars.quaternion.identity();
      bike.bars.position.set(0, 0, 0);
      const t = input.phase;
      if (kind === TRICK_KIND.flip && (variant === 0 || variant === 2 || variant === 3)) {
        const turns = variant === 2 ? 2 : 1;
        const axis = tmp.subVectors(bike.headTop, bike.headBottom).normalize();
        tmpQ.setFromAxisAngle(axis, Math.PI * 2 * turns * t);
        bike.frame.quaternion.copy(tmpQ);
        const pivot = bike.headBottom;
        bike.frame.position.copy(pivot).sub(pivot.clone().applyQuaternion(tmpQ));
      }
      if (kind === TRICK_KIND.flip && (variant === 1 || variant === 3)) {
        const axis = tmp.subVectors(bike.headTop, bike.headBottom).normalize();
        tmpQ.setFromAxisAngle(axis, Math.PI * 2 * t);
        bike.bars.quaternion.copy(tmpQ);
        bike.bars.position.copy(bike.headTop).sub(bike.headTop.clone().applyQuaternion(tmpQ));
      }
      if (kind === TRICK_KIND.grab && variant === 3) {
        this.vehicle.quaternion.setFromAxisAngle(Z, 1.1);
      }
      if (grinding) this.vehicle.position.y = -0.02;
    }
  }

  private poseSpinner(input: PoseInput, kind: number, variant: number): void {
    this.spinner.quaternion.identity();
    this.spinner.position.set(0, 0, 0);
    if (this.look.ride === 'inline' && kind === TRICK_KIND.flip) {
      const angle = Math.PI * 2 * input.phase;
      const axis =
        variant === 0
          ? Y
          : variant === 1
            ? new Vector3(1, 0, 1).normalize()
            : variant === 2
              ? Z
              : X;
      this.spinner.position.y = 0.9;
      this.spinner.quaternion.setFromAxisAngle(axis, variant === 3 ? -angle : angle);
      this.spinner.position.sub(new Vector3(0, 0.9, 0).applyQuaternion(this.spinner.quaternion));
    }
  }

  private updateLooseVehicle(dt: number, input: PoseInput): void {
    const parent = this.root.parent;
    if (!parent) return;
    if (!this.looseVehicle) {
      parent.attach(this.vehicle);
      const velocity = new Vector3(0, 3.5, 0);
      if (this.skate)
        velocity.add(new Vector3(0, 0, input.speed * 0.6).applyQuaternion(this.root.quaternion));
      this.looseVehicle = {
        velocity,
        spin: new Vector3(Math.random() * 8 - 4, Math.random() * 10 - 5, Math.random() * 8 - 4),
        grounded: false,
      };
    }
    const loose = this.looseVehicle;
    if (!loose.grounded) {
      loose.velocity.y -= 18 * dt;
      this.vehicle.position.addScaledVector(loose.velocity, dt);
      tmpQ.setFromAxisAngle(tmp.copy(loose.spin).normalize(), loose.spin.length() * dt);
      this.vehicle.quaternion.premultiply(tmpQ);
      const ground = input.groundAt?.(
        this.vehicle.position.x,
        this.vehicle.position.y + 0.5,
        this.vehicle.position.z,
      );
      if (ground !== undefined && this.vehicle.position.y <= ground) {
        this.vehicle.position.y = ground;
        if (Math.abs(loose.velocity.y) > 3) {
          loose.velocity.y *= -0.35;
          loose.velocity.x *= 0.7;
          loose.velocity.z *= 0.7;
          loose.spin.multiplyScalar(0.6);
        } else {
          loose.grounded = true;
          // Settle flat, keeping only its heading.
          const euler = new Vector3(0, 0, 1).applyQuaternion(this.vehicle.quaternion);
          const yaw = Math.atan2(euler.x, euler.z);
          this.vehicle.quaternion.setFromAxisAngle(Y, yaw);
          if (this.bike)
            this.vehicle.quaternion.multiply(tmpQ.setFromAxisAngle(Z, Math.PI / 2 - 0.1));
          if (this.bike) this.vehicle.position.y = ground + 0.02;
        }
      }
    } else {
      loose.velocity.multiplyScalar(Math.exp(-3 * dt));
      this.vehicle.position.x += loose.velocity.x * dt;
      this.vehicle.position.z += loose.velocity.z * dt;
    }
  }

  private applyTint(input: PoseInput): void {
    const hit = Math.max(0, input.hit);
    const pulse = input.protected ? 0.25 + Math.sin(this.time * 12) * 0.2 : 0;
    this.materials.forEach((material, index) => {
      const base = this.baseEmissive[index]!;
      material.emissive.setRGB(
        base.r + hit * 1.2 + pulse * 0.3,
        base.g + pulse * 0.6,
        base.b + pulse * 0.9,
      );
    });
  }

  public dispose(): void {
    if (this.looseVehicle) this.vehicle.removeFromParent();
    this.root.traverse((child) => {
      const mesh = child as Mesh;
      if (mesh.isMesh) {
        mesh.geometry.dispose();
      }
    });
    for (const material of this.materials) material.dispose();
    this.root.removeFromParent();
  }
}
